'use client'

import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import { Activity, ClipboardList, Contact, RefreshCw, Users } from 'lucide-react'

import type { RouterOutputs } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Button, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { DateRangeValue } from '../date-range-select'
import type { LessonSort } from './lesson-trend'
import { LessonTrendCards } from './lesson-trend'
import { MyCourses } from './my-courses'
import { ReadingJournalTable } from './reading-journal'
import { ReadingTrendTable } from './reading-trend'
import { StudentsTab } from './students'

export { StudentCard } from './lesson-trend'
export { LessonActivityChart, SubjectCompletionChart } from './lesson-trend'
export type { LessonSort, LessonTrendStudent } from './lesson-trend'
export { ConsistencyHistogram, GOOD_CONSISTENCY_PCT, ReadingActivityLineChart } from './reading-trend/charts'
export type { ActivityGranularity, ActivityPoint } from './reading-trend/charts'

const tabTriggerClassName = cn(
  'srd:group/tab-dash srd:relative srd:flex srd:h-16! srd:flex-1 srd:flex-col srd:items-stretch srd:min-h-0',
  'srd:gap-0! srd:p-0! srd:justify-between!',
  'srd:rounded-none srd:border-0 srd:border-transparent srd:bg-transparent',
  'srd:text-grey-70! srd:transition-colors srd:hover:text-grey-60 srd:data-active:text-navy-50!',
  'srd:after:hidden!',
)

const tabBodyClassName =
  'srd:flex srd:min-h-0 srd:flex-1 srd:flex-col srd:items-center srd:justify-center srd:gap-1 srd:px-1 srd:sm:gap-2 srd:sm:px-2'

const tabIndicatorClassName = cn(
  'srd:pointer-events-none srd:relative srd:z-1 srd:-mb-px srd:h-[3px] srd:w-full srd:max-w-[100px] srd:shrink-0 srd:self-center srd:rounded-t-full',
  'srd:bg-transparent srd:transition-colors srd:duration-200',
  'srd:group-data-active/tab-dash:bg-blue-70',
)

const tabLabelClassName = cn(
  typographyVariants({ variant: 'title-sm' }),
  'srd:hidden srd:text-center srd:text-current srd:transition-colors srd:sm:block',
)

export type DashboardTab = 'my-courses' | 'reading-journal' | 'aktivitas' | 'siswa-saya'

export type ActivityView = 'lesson' | 'reading'

export interface DashboardTabsProps {
  className?: string
  type: 'web' | 'school'
  defaultTab?: DashboardTab
  value?: DashboardTab
  onValueChange?: (value: DashboardTab) => void
  /** Controlled sub-view of the activity tab. Falls back to internal state when omitted. */
  activityView?: ActivityView
  onActivityViewChange?: (view: ActivityView) => void
  /** When provided, inserts the user's courses tab (`my-courses`) first. */
  coursesQuery?: UseQueryResult<RouterOutputs['lesson']['listTeacherCourses'], unknown>
  /** Required when coursesQuery is provided — called with courseId on card click. */
  onCourseClick?: (courseId: string) => void
  /** Required when coursesQuery is provided — builds the onClick for each dropdown menu item. */
  buildMenuAction?: (courseId: string, path: string) => () => void
  readingJournalQuery: UseQueryResult<RouterOutputs['metrics']['getReadingJournal'], unknown>
  /** Monday currently shown in the reading journal. Owned by the parent so week changes refetch server-side. */
  readingJournalWeekStart: Date
  /** Earliest selectable Monday for the reading journal (4 weeks before the current week). */
  readingJournalMinWeekStart: Date
  /** Called with the new Monday when the user changes the reading journal week. */
  onReadingJournalWeekChange: (monday: Date) => void
  /** School-class filter for the reading journal (`type === 'school'` only). `null` = all classes. */
  readingJournalSchoolClass?: string | null
  /** Called when the user picks a class; when set with `type === 'school'`, the class select is shown. */
  onReadingJournalSchoolClassChange?: (value: string | null) => void
  /** Member is not assigned to any class — the reading journal shows a distinct empty state with no CTA. */
  readingJournalNoClassAssigned?: boolean
  readingTrendQuery: UseQueryResult<RouterOutputs['metrics']['getReadingTrend'], unknown>
  /** Aggregate summary (charts + population stats) for the lesson-trend tab. */
  lessonTrendSummaryQuery: UseQueryResult<RouterOutputs['metrics']['getLessonTrend'], unknown>
  /** Cursor-paginated per-student cards for the lesson-trend tab. */
  lessonTrendStudentsQuery: UseInfiniteQueryResult<
    InfiniteData<RouterOutputs['metrics']['getLessonTrendStudents']>,
    unknown
  >
  /** Active sort for the lesson-trend cards. Owned by the parent so the students query refetches on change. */
  lessonSort: LessonSort
  onLessonSortChange: (sort: LessonSort) => void
  renderProfileLink: RenderLink
  renderCreateLink?: RenderLink
  withSearch?: boolean
  withDownloadButton?: boolean
  /** School-class filter shared across the Aktivitas tab (`type === 'school'` only). `null` = all classes. */
  activitySchoolClassId?: string | null
  onActivitySchoolClassChange?: (value: string | null) => void
  /** Resolved date range shared across the Aktivitas tab. Owned by the parent so trends refetch on change. */
  activityDateRange?: DateRangeValue
  onActivityDateRangeChange?: (range: DateRangeValue) => void
  /** Member is not assigned to any class — both Aktivitas sub-views (reading + lesson trend) show a distinct empty state with no CTA. */
  activityNoClassAssigned?: boolean
  /** First name of the signed-in user, used to personalise the no-students empty state in the Aktivitas tab. */
  userName?: string
  /** Empty-state "Jelajahi beranda" CTA. */
  renderExploreLink?: RenderLink
  /**
   * Rendered at the top of the students tab, above the roster.
   *
   * The app owns it because the numbers it shows are the app's: the school puts this row on its own
   * Kelola murid page, and the family wants the same three figures here.
   */
  studentsHeader?: React.ReactNode
}

export function DashboardTabs({
  className,
  type,
  defaultTab = 'aktivitas',
  value,
  onValueChange,
  activityView: activityViewProp,
  onActivityViewChange,
  coursesQuery,
  onCourseClick,
  buildMenuAction,
  readingJournalQuery,
  readingJournalWeekStart,
  readingJournalMinWeekStart,
  onReadingJournalWeekChange,
  readingJournalSchoolClass,
  onReadingJournalSchoolClassChange,
  readingJournalNoClassAssigned,
  readingTrendQuery,
  lessonTrendSummaryQuery,
  lessonTrendStudentsQuery,
  lessonSort,
  onLessonSortChange,
  renderProfileLink,
  renderCreateLink,
  renderExploreLink,
  studentsHeader,
  withSearch,
  withDownloadButton,
  activitySchoolClassId,
  onActivitySchoolClassChange,
  activityDateRange,
  onActivityDateRangeChange,
  activityNoClassAssigned,
  userName,
}: DashboardTabsProps) {
  const showMyCoursesTab = coursesQuery !== undefined
  const [internalActivityView, setInternalActivityView] = useState<ActivityView>('reading')
  const activityView = activityViewProp ?? internalActivityView
  const setActivityView = (view: ActivityView) => {
    if (activityViewProp === undefined) setInternalActivityView(view)
    onActivityViewChange?.(view)
  }

  const tabsProps =
    value !== undefined
      ? { value, onValueChange: (v: string) => onValueChange?.(v as DashboardTab) }
      : { defaultValue: defaultTab }

  return (
    <Tabs {...tabsProps} className={cn('srd:w-full srd:pt-1 srd:pb-20 srd:h-full', className)}>
      <TabsList
        variant='line'
        className={cn(
          'srd:w-full srd:rounded-none srd:border-b srd:border-grey-30 srd:bg-transparent',
          'srd:h-auto! srd:min-h-0! srd:p-0! srd:items-stretch srd:gap-0',
        )}
      >
        <TabsTrigger value='aktivitas' className={tabTriggerClassName}>
          <span className={tabBodyClassName}>
            <span className='srd:inline-flex srd:shrink-0 srd:text-current'>
              <Activity className='srd:size-6' />
            </span>
            <span className={tabLabelClassName}>Aktivitas</span>
          </span>
          <span aria-hidden className={tabIndicatorClassName} />
        </TabsTrigger>
        <TabsTrigger value='reading-journal' className={tabTriggerClassName}>
          <span className={tabBodyClassName}>
            <span className='srd:inline-flex srd:shrink-0 srd:text-current'>
              <Contact className='srd:size-6' />
            </span>
            <span className={tabLabelClassName}>Jurnal Baca</span>
          </span>
          <span aria-hidden className={tabIndicatorClassName} />
        </TabsTrigger>
        {showMyCoursesTab && (
          <TabsTrigger value='my-courses' className={tabTriggerClassName}>
            <span className={tabBodyClassName}>
              <span className='srd:inline-flex srd:shrink-0 srd:text-current'>
                <ClipboardList className='srd:size-6' />
              </span>
              <span className={tabLabelClassName}>Tugas Saya</span>
            </span>
            <span aria-hidden className={tabIndicatorClassName} />
          </TabsTrigger>
        )}
        <TabsTrigger value='siswa-saya' className={tabTriggerClassName}>
          <span className={tabBodyClassName}>
            <span className='srd:inline-flex srd:shrink-0 srd:text-current'>
              <Users className='srd:size-6' />
            </span>
            <span className={tabLabelClassName}>{type === 'web' ? 'Anak Saya' : 'Murid Saya'}</span>
          </span>
          <span aria-hidden className={tabIndicatorClassName} />
        </TabsTrigger>
      </TabsList>

      {showMyCoursesTab && coursesQuery && onCourseClick && buildMenuAction && (
        <TabsContent value='my-courses' keepMounted={false} className='srd:w-full srd:mt-6'>
          <MyCourses
            type={type}
            coursesQuery={coursesQuery}
            onCourseClick={onCourseClick}
            buildMenuAction={buildMenuAction}
            className='srd:mx-auto'
          />
        </TabsContent>
      )}

      <TabsContent value='reading-journal' keepMounted={false} className='srd:w-full srd:mt-6'>
        <QueryTabContent
          query={readingJournalQuery}
          render={(data) => (
            <ReadingJournalTable
              type={type}
              data={data}
              renderProfileLink={renderProfileLink}
              renderCreateLink={renderCreateLink}
              withSearch={withSearch}
              withDownloadButton={withDownloadButton}
              weekStart={readingJournalWeekStart}
              minWeekStart={readingJournalMinWeekStart}
              onWeekChange={onReadingJournalWeekChange}
              schoolClassValue={readingJournalSchoolClass}
              onSchoolClassChange={onReadingJournalSchoolClassChange}
              noClassAssigned={readingJournalNoClassAssigned}
            />
          )}
        />
      </TabsContent>

      <TabsContent value='aktivitas' keepMounted={false} className='srd:w-full srd:mt-6'>
        {activityView === 'lesson' ? (
          <QueryTabContent
            query={lessonTrendSummaryQuery}
            render={(summary) => (
              <LessonTrendCards
                type={type}
                summary={summary}
                studentsQuery={lessonTrendStudentsQuery}
                sort={lessonSort}
                onSortChange={onLessonSortChange}
                renderProfileLink={renderProfileLink}
                renderCreateLink={renderCreateLink}
                withSearch={withSearch}
                withDownloadButton={withDownloadButton}
                activityView={activityView}
                onActivityViewChange={setActivityView}
                schoolClassId={activitySchoolClassId}
                onSchoolClassChange={onActivitySchoolClassChange}
                dateRange={activityDateRange}
                onDateRangeChange={onActivityDateRangeChange}
                noClassAssigned={activityNoClassAssigned}
                userName={userName}
              />
            )}
          />
        ) : (
          <QueryTabContent
            query={readingTrendQuery}
            render={(data) => (
              <ReadingTrendTable
                type={type}
                data={data}
                renderProfileLink={renderProfileLink}
                renderCreateLink={renderCreateLink}
                withSearch={withSearch}
                withDownloadButton={withDownloadButton}
                activityView={activityView}
                onActivityViewChange={setActivityView}
                schoolClassId={activitySchoolClassId}
                onSchoolClassChange={onActivitySchoolClassChange}
                dateRange={activityDateRange}
                onDateRangeChange={onActivityDateRangeChange}
                noClassAssigned={activityNoClassAssigned}
                userName={userName}
              />
            )}
          />
        )}
      </TabsContent>

      <TabsContent value='siswa-saya' keepMounted={false} className='srd:w-full srd:mt-6'>
        {studentsHeader && <div className='srd:mb-6 srd:w-full'>{studentsHeader}</div>}

        <StudentsTab
          type={type}
          renderProfileLink={renderProfileLink}
          renderCreateLink={renderCreateLink}
          renderExploreLink={renderExploreLink}
        />
      </TabsContent>
    </Tabs>
  )
}

function QueryTabContent<T>({
  query,
  render,
}: {
  query: UseQueryResult<T, unknown>
  render: (data: T) => React.ReactNode
}) {
  if (query.isPending) {
    return (
      <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-3'>
        <Skeleton className='srd:h-10 srd:w-full srd:rounded-md' />
        <Skeleton className='srd:h-10 srd:w-full srd:rounded-md' />
        <Skeleton className='srd:h-10 srd:w-full srd:rounded-md' />
        <Skeleton className='srd:h-10 srd:w-full srd:rounded-md' />
        <Skeleton className='srd:h-10 srd:w-full srd:rounded-md' />
      </div>
    )
  }

  if (query.isError) {
    return (
      <div className='srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-3 srd:py-10 srd:text-center'>
        <p className='srd:text-sm srd:text-grey-100'>Gagal memuat data. Silakan coba lagi.</p>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void query.refetch()}
          disabled={query.isRefetching}
          className='srd:gap-x-2'
        >
          <RefreshCw className={cn('srd:size-3.5', query.isRefetching && 'srd:animate-spin')} />
          Coba lagi
        </Button>
      </div>
    )
  }

  const data = query.data
  if (data === undefined) return null
  return <>{render(data)}</>
}
